// =============================================================================
// Jobs Dashboard — live BullMQ queue monitoring
// -----------------------------------------------------------------------------
// Three views backed by /api/v1/admin/jobs:
//   Queues    — live counts per registered queue with a per-queue job
//               drill-down (failed/waiting/active/delayed/completed) and
//               one-click retry for failed jobs (job:manage).
//   Schedules — every repeatable tick with its interval and next fire time.
//   Runs      — ReconciliationRun history (sweep outcomes + counters).
// Live counts reflect Redis retention (removeOnComplete/removeOnFail); durable
// history lives on domain rows and ReconciliationRun.
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import {
  Badge,
  Button,
  Card,
  Select,
  Spinner,
  type BadgeVariant,
} from '@tkpod/ui';
import { hasPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';

// ---------------------------------------------------------------------------
// Types (mirror the admin jobs API)
// ---------------------------------------------------------------------------

interface QueueCounts {
  waiting: number;
  active: number;
  delayed: number;
  failed: number;
  completed: number;
}

interface QueueRow {
  name: string;
  kind: 'EVENT' | 'REPEATABLE';
  description: string;
  paused: boolean;
  /** Counts read failed (Redis unreachable) — zeros are placeholders. */
  unavailable: boolean;
  counts: QueueCounts;
}

interface JobRow {
  id: string;
  name: string;
  state: string;
  attemptsMade: number;
  timestamp: string | null;
  processedOn: string | null;
  finishedOn: string | null;
  delayedUntil: string | null;
  failedReason: string | null;
  data: Record<string, string | number>;
}

interface ScheduleRow {
  queue: string;
  jobName: string;
  every: number | null;
  pattern: string | null;
  nextRunAt: string | null;
}

interface RunRow {
  id: string;
  runType: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  intervalMs: number;
  counters: Record<string, unknown>;
  error: string | null;
}

interface JobsListResponse {
  items: JobRow[];
  meta: { page: number; limit: number; total: number };
}

interface RunsListResponse {
  items: RunRow[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

type JobState = 'failed' | 'waiting' | 'active' | 'delayed' | 'completed';

const JOB_STATE_OPTIONS: Array<{ value: JobState; label: string }> = [
  { value: 'failed', label: 'Failed' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'active', label: 'Active' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'completed', label: 'Completed' },
];

const RUN_STATUS_VARIANT: Record<string, BadgeVariant> = {
  RUNNING: 'info',
  COMPLETED: 'success',
  FAILED: 'danger',
};

function formatInterval(ms: number | null): string {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 === 0 ? `${hours}h` : `${hours}h ${minutes % 60}m`;
}

function formatDurationMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type TabId = 'queues' | 'schedules' | 'runs';

export function JobsPage() {
  const { user } = useAuth();
  const permissions =
    user?.permissions ?? user?.currentOrganization?.permissions ?? [];
  const canManageJobs = hasPermission(permissions, 'job:manage');
  const canReadJobs = hasPermission(permissions, 'job:read');

  const [tab, setTab] = useState<TabId>('queues');
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <Card padding="none">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">
              Job Queue Dashboard
            </h1>
            <p className="text-sm text-text-secondary">
              Live BullMQ queue status. Counts reflect Redis retention — most
              queues prune completed/failed jobs automatically; durable history
              is on the domain records.
            </p>
          </div>
          <div className="flex gap-1 rounded-md border border-border p-0.5">
            {(
              [
                ['queues', 'Queues'],
                ['schedules', 'Schedules'],
                ['runs', 'Runs'],
              ] as Array<[TabId, string]>
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={
                  tab === id
                    ? 'rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white'
                    : 'rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-tertiary'
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'queues' && (
          <QueuesTab
            canDrillDown={canReadJobs}
            selectedQueue={selectedQueue}
            onSelectQueue={setSelectedQueue}
          />
        )}
        {tab === 'schedules' && <SchedulesTab />}
        {tab === 'runs' && <RunsTab />}
      </Card>

      {tab === 'queues' && selectedQueue && canReadJobs && (
        <QueueJobsCard
          queue={selectedQueue}
          canRetry={canManageJobs}
          onClose={() => setSelectedQueue(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Queues tab
// ---------------------------------------------------------------------------

function QueuesTab({
  canDrillDown,
  selectedQueue,
  onSelectQueue,
}: {
  canDrillDown: boolean;
  selectedQueue: string | null;
  onSelectQueue: (name: string) => void;
}) {
  const api = useApiClient();

  const queuesQuery = useQuery<{ items: QueueRow[] }>({
    queryKey: ['admin', 'jobs', 'queues'],
    queryFn: () => api.get<{ items: QueueRow[] }>('/api/v1/admin/jobs/queues'),
    refetchInterval: 15_000,
  });

  if (queuesQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (queuesQuery.isError) {
    return (
      <p className="px-5 py-6 text-sm text-danger">
        {queuesQuery.error instanceof Error
          ? queuesQuery.error.message
          : 'Unable to load queues.'}
      </p>
    );
  }

  const rows = queuesQuery.data?.items ?? [];
  // Queues needing attention first, then alphabetical.
  const sorted = [...rows].sort((a, b) => {
    const aHot = a.unavailable || a.counts.failed > 0 || a.paused ? 0 : 1;
    const bHot = b.unavailable || b.counts.failed > 0 || b.paused ? 0 : 1;
    return aHot - bHot || a.name.localeCompare(b.name);
  });
  const unavailableCount = rows.filter((q) => q.unavailable).length;

  return (
    <div>
      {unavailableCount > 0 && (
        <div className="border-b border-border bg-danger/10 px-5 py-3 text-sm text-danger">
          {unavailableCount === rows.length
            ? 'Redis is unreachable — no queue could be read. Job processing is likely down; counts below are placeholders, not real values.'
            : `${unavailableCount} queue(s) could not be read — their counts are placeholders, not real values.`}
        </div>
      )}
      <table className="w-full text-left text-sm">
      <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
        <tr>
          <th className="px-5 py-2 font-semibold">Queue</th>
          <th className="px-3 py-2 font-semibold">Kind</th>
          <th className="px-3 py-2 text-right font-semibold">Waiting</th>
          <th className="px-3 py-2 text-right font-semibold">Active</th>
          <th className="px-3 py-2 text-right font-semibold">Delayed</th>
          <th className="px-3 py-2 text-right font-semibold">Failed</th>
          <th className="px-3 py-2 text-right font-semibold">Completed</th>
          <th className="px-5 py-2 text-right font-semibold">Actions</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((q) => (
          <tr
            key={q.name}
            className={
              q.name === selectedQueue
                ? 'border-b border-border/60 bg-surface-tertiary/60'
                : 'border-b border-border/60'
            }
          >
            <td className="px-5 py-2.5">
              <div className="flex items-center gap-2">
                <code className="font-mono text-text-primary">{q.name}</code>
                {q.paused && (
                  <Badge variant="warning" size="sm">
                    paused
                  </Badge>
                )}
                {q.unavailable && (
                  <Badge variant="danger" size="sm">
                    unreachable
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-xs text-text-tertiary">
                {q.description}
              </p>
            </td>
            <td className="px-3 py-2.5">
              <Badge
                variant={q.kind === 'REPEATABLE' ? 'purple' : 'default'}
                size="sm"
              >
                {q.kind === 'REPEATABLE' ? 'tick' : 'event'}
              </Badge>
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {q.unavailable ? '—' : q.counts.waiting}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {q.unavailable ? '—' : q.counts.active}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {q.unavailable ? '—' : q.counts.delayed}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {q.unavailable ? (
                '—'
              ) : q.counts.failed > 0 ? (
                <span className="font-semibold text-danger">
                  {q.counts.failed}
                </span>
              ) : (
                q.counts.failed
              )}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums text-text-tertiary">
              {q.unavailable ? '—' : q.counts.completed}
            </td>
            <td className="px-5 py-2.5 text-right">
              {canDrillDown && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSelectQueue(q.name)}
                >
                  Inspect
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-queue job drill-down
// ---------------------------------------------------------------------------

function QueueJobsCard({
  queue,
  canRetry,
  onClose,
}: {
  queue: string;
  canRetry: boolean;
  onClose: () => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [state, setState] = useState<JobState>('failed');
  const [page, setPage] = useState(1);
  const limit = 25;

  const jobsQuery = useQuery<JobsListResponse>({
    queryKey: ['admin', 'jobs', 'queue-jobs', queue, state, page],
    queryFn: () =>
      api.get<JobsListResponse>(
        `/api/v1/admin/jobs/queues/${encodeURIComponent(queue)}/jobs?state=${state}&page=${page}&limit=${limit}`,
      ),
    refetchInterval: 15_000,
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: string) =>
      api.post(
        `/api/v1/admin/jobs/queues/${encodeURIComponent(queue)}/jobs/${encodeURIComponent(jobId)}/retry`,
        {},
      ),
    onSuccess: () => {
      toast('Job re-queued', 'success');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : 'Retry failed', 'error');
    },
  });

  const rows = jobsQuery.data?.items ?? [];
  const total = jobsQuery.data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-text-primary">
            <code className="font-mono">{queue}</code> jobs
          </h2>
          <div className="w-36">
            <Select
              aria-label="Job state"
              options={JOB_STATE_OPTIONS}
              value={state}
              onChange={(e) => {
                setState(e.target.value as JobState);
                setPage(1);
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => jobsQuery.refetch()}
            disabled={jobsQuery.isFetching}
          >
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {jobsQuery.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : jobsQuery.isError ? (
        <p className="px-5 py-6 text-sm text-danger">
          {jobsQuery.error instanceof Error
            ? jobsQuery.error.message
            : 'Unable to load jobs.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-text-secondary">
          No {state} jobs in this queue.
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
            <tr>
              <th className="px-5 py-2 font-semibold">Job</th>
              <th className="px-3 py-2 font-semibold">Payload</th>
              <th className="px-3 py-2 font-semibold">Attempts</th>
              <th className="px-3 py-2 font-semibold">Enqueued</th>
              <th className="px-3 py-2 font-semibold">
                {state === 'delayed' ? 'Runs at' : 'Finished'}
              </th>
              {state === 'failed' && (
                <th className="px-3 py-2 font-semibold">Failure</th>
              )}
              {state === 'failed' && canRetry && (
                <th className="px-5 py-2 text-right font-semibold">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((job) => (
              <tr key={job.id} className="border-b border-border/60 align-top">
                <td className="px-5 py-2.5">
                  <p className="font-medium text-text-primary">{job.name}</p>
                  <code className="font-mono text-[11px] text-text-tertiary">
                    {job.id}
                  </code>
                </td>
                <td className="max-w-xs px-3 py-2.5 text-xs text-text-secondary">
                  {Object.keys(job.data).length > 0 ? (
                    <p className="break-all font-mono text-[11px]">
                      {Object.entries(job.data)
                        .map(([k, v]) => `${k}=${String(v)}`)
                        .join(' • ')}
                    </p>
                  ) : (
                    <span className="text-text-tertiary">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 tabular-nums">{job.attemptsMade}</td>
                <td className="px-3 py-2.5 text-xs text-text-secondary">
                  {job.timestamp ? new Date(job.timestamp).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2.5 text-xs text-text-secondary">
                  {state === 'delayed'
                    ? job.delayedUntil
                      ? new Date(job.delayedUntil).toLocaleString()
                      : '—'
                    : job.finishedOn
                      ? new Date(job.finishedOn).toLocaleString()
                      : '—'}
                </td>
                {state === 'failed' && (
                  <td className="max-w-sm px-3 py-2.5 text-xs text-danger">
                    <p className="break-all">{job.failedReason ?? '—'}</p>
                  </td>
                )}
                {state === 'failed' && canRetry && (
                  <td className="px-5 py-2.5 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={retryMutation.isPending}
                      onClick={() => retryMutation.mutate(job.id)}
                    >
                      Retry
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm">
          <span className="text-text-tertiary">
            Page {page} of {totalPages} • {total} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Schedules tab
// ---------------------------------------------------------------------------

function SchedulesTab() {
  const api = useApiClient();

  const schedulesQuery = useQuery<{ items: ScheduleRow[] }>({
    queryKey: ['admin', 'jobs', 'schedules'],
    queryFn: () =>
      api.get<{ items: ScheduleRow[] }>('/api/v1/admin/jobs/schedules'),
    refetchInterval: 60_000,
  });

  if (schedulesQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (schedulesQuery.isError) {
    return (
      <p className="px-5 py-6 text-sm text-danger">
        {schedulesQuery.error instanceof Error
          ? schedulesQuery.error.message
          : 'Unable to load schedules.'}
      </p>
    );
  }

  const rows = schedulesQuery.data?.items ?? [];
  if (rows.length === 0) {
    return (
      <p className="px-5 py-14 text-center text-sm text-text-secondary">
        No repeatable schedules registered. The workers process registers them
        at boot — if this list is empty, workers may not be running.
      </p>
    );
  }

  const now = Date.now();

  return (
    <table className="w-full text-left text-sm">
      <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
        <tr>
          <th className="px-5 py-2 font-semibold">Queue</th>
          <th className="px-3 py-2 font-semibold">Job</th>
          <th className="px-3 py-2 font-semibold">Interval</th>
          <th className="px-3 py-2 font-semibold">Next run</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => {
          const next = s.nextRunAt ? new Date(s.nextRunAt).getTime() : null;
          // A next-run more than one interval in the past ⇒ tick is stalled.
          const overdue =
            next != null && s.every != null && now - next > s.every;
          return (
            <tr
              key={`${s.queue}:${s.jobName}:${s.nextRunAt ?? ''}`}
              className="border-b border-border/60"
            >
              <td className="px-5 py-2.5">
                <code className="font-mono text-text-primary">{s.queue}</code>
              </td>
              <td className="px-3 py-2.5 text-text-secondary">{s.jobName}</td>
              <td className="px-3 py-2.5 tabular-nums">
                {s.pattern ? (
                  <code className="font-mono text-xs">{s.pattern}</code>
                ) : (
                  formatInterval(s.every)
                )}
              </td>
              <td className="px-3 py-2.5 text-xs">
                {s.nextRunAt ? (
                  <span className={overdue ? 'font-medium text-danger' : ''}>
                    {new Date(s.nextRunAt).toLocaleString()}
                    {overdue ? ' (overdue)' : ''}
                  </span>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Runs tab (ReconciliationRun history)
// ---------------------------------------------------------------------------

function RunsTab() {
  const api = useApiClient();
  const [page, setPage] = useState(1);
  const [runType, setRunType] = useState('');

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', '25');
  if (runType) params.set('runType', runType);

  const runsQuery = useQuery<RunsListResponse>({
    queryKey: ['admin', 'jobs', 'runs', page, runType],
    queryFn: () =>
      api.get<RunsListResponse>(`/api/v1/admin/jobs/runs?${params}`),
    refetchInterval: 30_000,
  });

  const rows = runsQuery.data?.items ?? [];
  const meta = runsQuery.data?.meta;

  // Distinct runTypes present on the current page (cheap filter source).
  const runTypeOptions = [
    { value: '', label: 'All sweeps' },
    ...Array.from(new Set(rows.map((r) => r.runType)))
      .sort()
      .map((t) => ({ value: t, label: t })),
  ];
  if (runType && !runTypeOptions.some((o) => o.value === runType)) {
    runTypeOptions.push({ value: runType, label: runType });
  }

  return (
    <div>
      <div className="flex items-center justify-end gap-2 border-b border-border px-5 py-3">
        <div className="w-56">
          <Select
            aria-label="Sweep type filter"
            options={runTypeOptions}
            value={runType}
            onChange={(e) => {
              setRunType(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => runsQuery.refetch()}
          disabled={runsQuery.isFetching}
        >
          Refresh
        </Button>
      </div>

      {runsQuery.isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner />
        </div>
      ) : runsQuery.isError ? (
        <p className="px-5 py-6 text-sm text-danger">
          {runsQuery.error instanceof Error
            ? runsQuery.error.message
            : 'Unable to load runs.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-14 text-center text-sm text-text-secondary">
          No reconciliation runs recorded yet.
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
            <tr>
              <th className="px-5 py-2 font-semibold">Sweep</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Started</th>
              <th className="px-3 py-2 font-semibold">Duration</th>
              <th className="px-3 py-2 font-semibold">Interval</th>
              <th className="px-3 py-2 font-semibold">Counters</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((run) => {
              const counters = Object.entries(run.counters ?? {}).filter(
                ([, v]) => typeof v === 'number' || typeof v === 'string',
              );
              return (
                <tr key={run.id} className="border-b border-border/60 align-top">
                  <td className="px-5 py-2.5">
                    <code className="font-mono text-text-primary">
                      {run.runType}
                    </code>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge
                      variant={RUN_STATUS_VARIANT[run.status] ?? 'default'}
                      size="sm"
                    >
                      {run.status}
                    </Badge>
                    {run.error && (
                      <p className="mt-1 max-w-xs break-all text-[11px] text-danger">
                        {run.error}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-text-secondary">
                    {new Date(run.startedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-xs tabular-nums">
                    {formatDurationMs(run.durationMs)}
                  </td>
                  <td className="px-3 py-2.5 text-xs tabular-nums">
                    {formatInterval(run.intervalMs)}
                  </td>
                  <td className="max-w-md px-3 py-2.5 text-xs text-text-secondary">
                    {counters.length > 0 ? (
                      <p className="break-all font-mono text-[11px]">
                        {counters
                          .map(([k, v]) => `${k}=${String(v)}`)
                          .join(' • ')}
                      </p>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm">
          <span className="text-text-tertiary">
            Page {meta.page} of {meta.totalPages} • {meta.total} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
